/**
 * Shared Google-map primitives.
 *
 * Pulled out of the Scheduling Console's map view so the itinerary's trip
 * route can draw the same way rather than growing a second, slightly
 * different implementation of "fit the pins" and "draw the line". Both are
 * imperative because @vis.gl 1.x exposes no declarative equivalent, and both
 * tear down on change so a re-render never leaves an orphaned overlay on the
 * map instance.
 */

import { useEffect } from 'react'
import { useMap } from '@vis.gl/react-google-maps'

export type LatLng = { lat: number; lng: number }

/** Frame the map around a set of points; a single point just centres. */
export function FitBounds({
  points,
  padding = 64,
  singleZoom = 13,
}: {
  points: LatLng[]
  padding?: number
  singleZoom?: number
}) {
  const map = useMap()
  // Key on the coordinates, not the array identity: a parent re-render hands
  // us a fresh array every time and would re-frame the map under the user.
  const key = points.map((p) => `${p.lat},${p.lng}`).join('|')
  useEffect(() => {
    if (!map || points.length === 0) return
    if (points.length === 1) {
      map.setCenter(points[0])
      map.setZoom(singleZoom)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    points.forEach((p) => bounds.extend(p))
    map.fitBounds(bounds, padding)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key, padding, singleZoom])
  return null
}

/**
 * A path drawn between points in visiting order.
 *
 * `strokeColor` is a literal colour, not a CSS variable - the Maps API
 * rasterises the line itself and never sees our stylesheet.
 */
export function RoutePath({
  path,
  strokeColor = '#3a3a36',
  strokeOpacity = 0.7,
  strokeWeight = 3,
}: {
  path: LatLng[]
  strokeColor?: string
  strokeOpacity?: number
  strokeWeight?: number
}) {
  const map = useMap()
  const key = path.map((p) => `${p.lat},${p.lng}`).join('|')
  useEffect(() => {
    if (!map || path.length < 2) return
    const line = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor,
      strokeOpacity,
      strokeWeight,
    })
    line.setMap(map)
    return () => line.setMap(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key, strokeColor, strokeOpacity, strokeWeight])
  return null
}

/**
 * Numbered pins, drawn imperatively.
 *
 * `AdvancedMarker` is the modern component and it hard-requires a Cloud map
 * id, so a map without one gets no pins at all. The classic marker has no
 * such dependency and takes a label directly, which is exactly what a
 * visiting order needs. Icon is an inline SVG data URI so the colour can
 * vary per day without shipping image assets.
 */
export function NumberedMarkers({
  pins,
}: {
  pins: { key: string; point: LatLng; ordinal: number; colour: string; title: string }[]
}) {
  const map = useMap()
  // Key on the content, not the array identity - a parent re-render would
  // otherwise tear down and rebuild every pin on the map.
  const key = pins.map((p) => `${p.key}:${p.ordinal}:${p.colour}`).join('|')
  useEffect(() => {
    if (!map || pins.length === 0) return
    const markers = pins.map(
      (pin) =>
        new google.maps.Marker({
          map,
          position: pin.point,
          title: pin.title,
          label: {
            text: String(pin.ordinal),
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: '600',
          },
          icon: {
            url:
              'data:image/svg+xml;charset=UTF-8,' +
              encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">` +
                  `<circle cx="15" cy="15" r="13" fill="${pin.colour}" stroke="#ffffff" stroke-width="2.5"/>` +
                  `</svg>`,
              ),
            scaledSize: new google.maps.Size(30, 30),
            anchor: new google.maps.Point(15, 15),
            labelOrigin: new google.maps.Point(15, 15),
          },
        }),
    )
    return () => markers.forEach((m) => m.setMap(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key])
  return null
}
